import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { wetUniform } from './conditions.js';

type Surface = 'wood' | 'stone' | 'plaster' | 'cloth' | 'thatch' | 'roof';
/** All matte, but not alike: worn wood and fired roof tiles keep a little more sheen than lime plaster, straw or cloth. */
const SURFACE_ROUGHNESS: Record<Surface, number> = { wood: .84, stone: .9, plaster: .97, cloth: .98, thatch: 1, roof: .8 };

/** Procedural wear stays crisp at game zoom and needs no texture downloads. */
export function weatheredMaterial(color: number, surface: Surface, extra: THREE.MeshStandardMaterialParameters = {}) {
  const material = new THREE.MeshStandardMaterial({ color, roughness: SURFACE_ROUGHNESS[surface], ...extra });
  material.customProgramCacheKey = () => `weathered-${surface}-v2`;
  material.onBeforeCompile = shader => {
    shader.uniforms.uWet = wetUniform;
    // Rain darkens the surface and lends it a sheen; the shade stays matte where it was rough to begin with.
    shader.fragmentShader = shader.fragmentShader.replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\n  roughnessFactor *= 1. - .45 * uWet;');
    shader.vertexShader = 'varying vec3 vDetailAt;\n' + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nvDetailAt = position;');
    shader.fragmentShader = `varying vec3 vDetailAt;
      float detailHash(vec3 p) { return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453); }
      float detailNoise(vec3 p) {
        vec3 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
        return mix(mix(mix(detailHash(i), detailHash(i + vec3(1,0,0)), f.x), mix(detailHash(i + vec3(0,1,0)), detailHash(i + vec3(1,1,0)), f.x), f.y),
          mix(mix(detailHash(i + vec3(0,0,1)), detailHash(i + vec3(1,0,1)), f.x), mix(detailHash(i + vec3(0,1,1)), detailHash(i + vec3(1,1,1)), f.x), f.y), f.z);
      }\nuniform float uWet;\n` + shader.fragmentShader.replace('#include <color_fragment>', `#include <color_fragment>
        diffuseColor.rgb *= 1. - .18 * uWet;
        vec3 dp = vDetailAt;
        ${surface === 'wood' ? `
          float grain = sin(dp.y * 1.2 + detailNoise(dp * vec3(3., .3, 3.)) * 14. + (dp.x + dp.z) * 32.);
          float split = smoothstep(.86, .99, grain) * (.4 + .6 * detailNoise(dp * vec3(2., 5., 2.)));
          diffuseColor.rgb *= .9 + detailNoise(dp * 2.) * .18 - split * .19;
        ` : surface === 'cloth' ? `
          float weave = sin(dp.x * 100.) * sin(dp.y * 100.);
          diffuseColor.rgb *= .95 + weave * .025 + detailNoise(dp * 3.) * .08;
        ` : surface === 'roof' ? `
          vec2 tile = dp.xz * vec2(2.1, 3.3); tile.x += mod(floor(tile.y), 2.) * .5;
          vec2 tf = fract(tile); float seam = 1. - smoothstep(.025, .075, min(tf.x, min(tf.y, 1. - tf.y)));
          diffuseColor.rgb *= (.87 + detailHash(vec3(floor(tile), 1.)) * .2) * (1. - seam * .2);
        ` : surface === 'thatch' ? `
          float straw = sin((dp.x + dp.z) * 42. + detailNoise(dp * 3.) * 5.);
          diffuseColor.rgb *= .9 + straw * .055 + detailNoise(dp * 5.) * .13;
        ` : `
          float patches = detailNoise(dp * ${surface === 'plaster' ? '1.6' : '2.8'});
          diffuseColor.rgb *= .87 + patches * .23;
          diffuseColor.rgb *= 1. - smoothstep(.72, .9, detailNoise(dp * 12.)) * .1;
        `}`);
  };
  return material;
}

/** Only call on wholly static prop subtrees. Moving lanterns, cloth and flames stay separate. */
export function mergeStaticPropMeshes(group: THREE.Group) {
  group.updateMatrixWorld(true);
  const inverse = group.matrixWorld.clone().invert();
  const batches = new Map<string, { material: THREE.Material; source: THREE.Mesh[]; geometry: THREE.BufferGeometry[]; shadow: boolean; receive: boolean }>();
  group.traverse(object => {
    if (!(object instanceof THREE.Mesh) || object instanceof THREE.InstancedMesh || Array.isArray(object.material) || object.material.transparent) return;
    const key = `${object.material.uuid}:${object.castShadow}:${object.receiveShadow}`;
    let batch = batches.get(key);
    if (!batch) { batch = { material: object.material, source: [], geometry: [], shadow: object.castShadow, receive: object.receiveShadow }; batches.set(key, batch); }
    let geometry = object.geometry.clone().applyMatrix4(new THREE.Matrix4().multiplyMatrices(inverse, object.matrixWorld));
    if (geometry.index) { const expanded = geometry.toNonIndexed(); geometry.dispose(); geometry = expanded; }
    // All these solid props have normals; UVs are unused by their procedural materials.
    geometry.deleteAttribute('uv'); batch.geometry.push(geometry); batch.source.push(object);
  });
  for (const batch of batches.values()) {
    if (batch.source.length < 2) { batch.geometry.forEach(geometry => geometry.dispose()); continue; }
    const geometry = mergeGeometries(batch.geometry);
    batch.geometry.forEach(part => part.dispose());
    if (!geometry) continue;
    const mesh = new THREE.Mesh(geometry, batch.material); mesh.castShadow = batch.shadow; mesh.receiveShadow = batch.receive; group.add(mesh);
    const retired = new Set<THREE.BufferGeometry>();
    for (const source of batch.source) { source.removeFromParent(); retired.add(source.geometry); }
    retired.forEach(part => part.dispose());
  }
}

/** Bulging barrel with inset plank lid, stave seams, iron hoops and a bung. */
export function detailedBarrel(parent: THREE.Group, wood: THREE.Material, iron: THREE.Material, x: number, z: number, y = .65) {
  const group = new THREE.Group(); group.position.set(x, y, z); parent.add(group);
  const add = (geometry: THREE.BufferGeometry, material: THREE.Material, height = 0) => { const mesh = new THREE.Mesh(geometry, material); mesh.position.y = height; mesh.castShadow = mesh.receiveShadow = true; group.add(mesh); return mesh; };
  const body = new THREE.LatheGeometry([new THREE.Vector2(.39, -.65), new THREE.Vector2(.46, -.53), new THREE.Vector2(.51, -.18), new THREE.Vector2(.51, .18), new THREE.Vector2(.46, .53), new THREE.Vector2(.39, .65)], 12);
  add(body, wood);
  for (const height of [-.48, .48]) add(new THREE.CylinderGeometry(.474, .474, .075, 12, 1, true), iron, height);
  add(new THREE.CylinderGeometry(.395, .395, .045, 12), wood, .625);
  for (const offset of [-.2, 0, .2]) { const seam = add(new THREE.BoxGeometry(.014, .008, Math.sqrt(.38 ** 2 - offset ** 2) * 2), iron, .652); seam.position.x = offset; }
  for (let i = 0; i < 12; i++) {
    const a = i / 12 * Math.PI * 2;
    const seam = add(new THREE.BoxGeometry(.015, .8, .018), iron); seam.position.set(Math.sin(a) * .503, 0, Math.cos(a) * .503); seam.rotation.y = a;
  }
  const bung = add(new THREE.CylinderGeometry(.055, .055, .035, 6), wood, .665); bung.position.x = .17;
  mergeStaticPropMeshes(group);
}

/** An irregular scalloped crown, shared by all instanced pines. Original cone bounds are retained. */
export function pineCrownGeometry() {
  const positions: number[] = [], colors: number[] = [], indices: number[] = [], sides = 10;
  const rings = [{ y: -.5, r: 1, shade: .83 }, { y: -.08, r: .39, shade: .98 }, { y: -.08, r: .65, shade: .84 }, { y: .5, r: .015, shade: 1.06 }];
  for (const [ring, profile] of rings.entries()) for (let i = 0; i < sides; i++) {
    const angle = i / sides * Math.PI * 2, radius = profile.r * (i % 2 ? .84 : 1);
    positions.push(Math.sin(angle) * radius, profile.y + (ring === 0 ? (i % 2) * .17 : 0), Math.cos(angle) * radius);
    colors.push(profile.shade, profile.shade, profile.shade);
    if (ring < rings.length - 1) { const a = ring * sides + i, b = ring * sides + (i + 1) % sides; indices.push(a, b, a + sides, b, b + sides, a + sides); }
  }
  const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3)); geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3)); geometry.setIndex(indices);
  const faceted = geometry.toNonIndexed(); geometry.dispose(); faceted.computeVertexNormals(); return faceted;
}
